/* Internal dependencies */
import React, { useCallback, useState } from 'react';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import get from 'lodash/get';

/* Internal dependencies */
import { ApplicationState } from 'src/store';
import Icon, { Icons } from 'src/icon/Icon';
import Colors from 'src/colors';
import Button from 'src/button/Button';
import AuthModalButton from 'src/authModal/AuthModalButton';
import { addNotification } from 'src/store/ducks/notifications';
import { Notification } from 'src/types/Notification';
import { getUser, updateUser } from 'src/store/ducks/users';
import { removeUserFollow, toggleUserFollow } from 'src/store/stories/users';
import { cleanObject } from 'src/common/cleanObject';
import Analytics, { AnalyticsEventType } from 'src/analytics/Analytics';
import { fetchAll } from 'src/common/helpers/fetchAll';
import { ParticipantStatus } from 'src/types/Participant';
import User from 'src/types/User';
import { CurrentUserState, getCurrentUser } from 'src/store/ducks/currentUser';
import { CurrentProfileState, getCurrentProfile } from 'src/store/ducks/currentProfile';

type OwnProps = {
  user: User;
  iconSize?: number;
  showIcon?: boolean;
  render?: (params: {
    onClick: Function;
    followed: boolean;
    followedStatus?: ParticipantStatus;
    followingYouStatus?: ParticipantStatus;
    loading: boolean;
  }) => React.ReactNode;
  onRemoveFollow?: () => void;
  onRemoveFollowRequest?: () => void;
  onRejectFollowRequest?: () => void;
  onAcceptFollowRequest?: () => void;
  activeBorderColor?: Colors;
  inactiveBorderColor?: Colors;
  activeBackgroundColor?: Colors;
  inactiveBackgroundColor?: Colors;
  activeTextColor?: Colors;
  inactiveTextColor?: Colors;
  fontSize?: string;
  width?: number | string;
};

type StateProps = {
  user: User;
  currentUser: CurrentUserState['user'];
  currentProfile: CurrentProfileState;
};

type DispatchProps = {
  addNotification(notification: Omit<Notification, 'id'>): void;
  updateUser(user: User): void;
};

type Props = OwnProps & StateProps & DispatchProps;

type State = {
  loading: boolean;
};

const FollowUserButton: React.FC<Props> = ({
  user, showIcon = true,
  iconSize = 22,
  render,
  onRemoveFollow,
  onRemoveFollowRequest,
  onRejectFollowRequest,
  onAcceptFollowRequest,
  activeBorderColor = Colors.white,
  inactiveBorderColor = Colors.inactiveTab,
  activeBackgroundColor = Colors.transparent,
  inactiveBackgroundColor = Colors.primary,
  activeTextColor = Colors.white,
  inactiveTextColor = Colors.white,
  fontSize = '1.25rem',
  width,
  updateUser,
}) => {
  const [loading, setLoading] = useState<State['loading']>(false);

  const userId = get(user, 'id');
  const userFollowed = get(user, 'followed', false);
  const userFollowedStatus = get(user, 'followedStatus');
  const userFollowingYouStatus = get(user, 'followingYouStatus');
  const isPending = Boolean(userFollowedStatus === ParticipantStatus.pending);
  const isConfirmed = Boolean(userFollowedStatus === ParticipantStatus.confirmed);

  const handleFollow = useCallback(async () => {
    try {
      setLoading(true);
      const promises: Promise<any>[] = [];
  
      if (userFollowed) {
        promises.push(Analytics.track(AnalyticsEventType.userUnfollowed, { userId }));
      } else {
        if (userFollowingYouStatus === ParticipantStatus.confirmed) {
          promises.push(Analytics.track(AnalyticsEventType.userFollowedBack, { userId }));
        } else {
          promises.push(Analytics.track(AnalyticsEventType.userFollowed, { userId }));
        }
      }
  
      promises.push(toggleUserFollow(user));

      return await fetchAll(promises);
    } finally {
      setLoading(false);
    }
  }, [userId, userFollowed, userFollowedStatus, userFollowingYouStatus, user]);

  const handleRemoveFollow = async () => {
    removeUserFollow(user);

    onRemoveFollow && onRemoveFollow();

    updateUser({
      ...user,
      followedStatus: undefined,
    });

    Analytics.track(AnalyticsEventType.userFollowed, { userId: user.id });

    addNotification({
      title: 'User unfollowed',
      message: `You unfollowed ${get(user, 'name', 'the user')}.`,
      items: [user],
      variant: 'primary',
    });
  };

  const handleAcceptFollowRequest = async () => {
    // TODO: Finish this
    // acceptFollowRequestForUser(user.id);

    onAcceptFollowRequest && onAcceptFollowRequest();

    updateUser({
      ...user,
      followedStatus: ParticipantStatus.confirmed,
    });

    Analytics.track(AnalyticsEventType.followRequestAccepted, { userId: user.id });

    addNotification({
      title: 'Accepted follow request',
      message: 'Accepted follow request.',
      variant: 'primary',
      items: [user],
    });
  };

  const handleRejectFollowRequest = async () => {
    // TODO: Finish this
    // rejectFollowRequestForUser(user.id);

    onRejectFollowRequest && onRejectFollowRequest();

    updateUser({
      ...user,
      followedStatus: undefined,
    });
    
    Analytics.track(AnalyticsEventType.followRequestRejected, { userId: user.id });

    addNotification({
      title: 'Declined follow request',
      message: 'Declined follow request.',
      variant: 'primary',
      items: [user],
    });
  };

  const followed = get(user, 'followed', false);

  return (
    <AuthModalButton
      modalHeader={`Register / Sign in to follow ${get(user, 'name', 'user')}`}
      modalSubheader="Get notified about upcoming events, exclusive offers, and support the user."
      onClick={handleFollow}
      render={(onClick) => {

        if (render) {
          return render({ onClick, followed, loading });
        }

        return (
          <Button
            disabled={!user}
            className="btn btn-block d-flex justify-content-center align-items-center"
            variant="primary"
            onClick={onClick}
            style={cleanObject({
              width,
              borderRadius: 100,
              borderColor: Boolean(followed) ? activeBorderColor : inactiveBorderColor,
              borderWidth: Boolean(followed) ? 2.5 : 0,
              backgroundColor: Boolean(followed) ? activeBackgroundColor : inactiveBackgroundColor,
            })}
          >
            <div className="d-flex align-items-center">
              {Boolean(showIcon) && (
                <div className="mr-2">
                  {Boolean(followed) ? (
                    <Icon name={Icons.check} className="bm-Icon--white" solid size={iconSize} />
                  ) : (
                    <Icon name={Icons.plus} className="bm-Icon--white" size={iconSize} />
                  )}
                </div>
              )}
              <h5 className="p-0 m-0 text-bold" style={{ fontSize, color: Boolean(followed) ? activeTextColor : inactiveTextColor }}>{Boolean(followed) ? 'Following' : 'Follow'}</h5>
            </div>
          </Button>
        );
      }}
    />
  );
};

const mapStateToProps = (state: ApplicationState, { user }: OwnProps) => {
  const userId = get(user, 'id');

  return {
    currentUser: getCurrentUser(state),
    currentProfile: getCurrentProfile(state),
    user: getUser(state, userId) || user,
  };
};

const mapDispatchToProps = (dispatch: Dispatch) => ({
  addNotification: (notification: Omit<Notification, 'id'>) => {
    dispatch(addNotification(notification));
  },
  updateUser: (user: User) => { dispatch(updateUser(user)); },
});

export default connect(mapStateToProps, mapDispatchToProps)(FollowUserButton);